/**
 * Interpolação dos OUTROS jogadores, e do corpo que NEVER é meu.
 *
 * O problema é o irmão do que a predição resolve, virado do avesso. O próprio avatar
 * responde na hora porque o cliente sabe a intenção do dono. A dos outros ele NEVER
 * sabe — e é por isso que aqui NEVER se prevê nada. O que dá para fazer é desenhar o
 * PASSADO deles, atrasado de `BUD-interp`, e usar esse atraso para ter sempre dois
 * snapshots emoldurando o instante desenhado. Entre eles, uma reta.
 *
 * A identidade que isto mantém, e que o gate assere:
 *
 *     desenhado(t) == autoridade(t − BUD-interp)
 *
 * Ou seja: nada de posição inventada. Cada quadro mostra um instante que a autoridade
 * DE FATO simulou — só que um pouco atrás. Extrapolar seria inventar, e inventar a
 * intenção do outro é o que produz o jogador que anda para dentro da parede e volta.
 *
 * O CORPO entra aqui pela mesma porta, e por um motivo que o `ADR-0017` já tinha
 * escrito: enquanto o campo do companheiro é que o move, a intenção que o move é dele
 * — ou seja, é exatamente a situação dos outros jogadores. O corpo que ESTE cliente
 * puxa sozinho NEVER passa por aqui: quem o desenha é a predição, e quem decide de
 * qual dos dois ele vem é `canPredictBody`, com a autoridade mais NOVA na mão.
 *
 * Invariante 2: nada aqui vai para o fio, e nada aqui decide acerto.
 * Caminho quente, uma vez por frame: em regime nada aqui aloca. O que ainda aloca é o
 * ENCHIMENTO do anel — cada quadro cresce até a maior sala que já viu, e para. Corpo
 * NEVER tem teto como `maxPlayers` tem: o número é conteúdo, e um teto inventado aqui
 * seria número de código decidindo o que a sala pode ter.
 */
import { balance } from '@game/gamedata';
import { INVARIANT_BUDGETS } from '@game/gamedata/perf';
import type { MessageData } from '@game/protocol';

type AuthoritativeSnapshot = MessageData<'snapshot'>;

/** Milissegundos de servidor por tick. O `tick` do snapshot É o relógio da autoridade. */
const TICK_MS = 1000 / balance.tickRate;
const SNAPSHOT_INTERVAL_MS = 1000 / balance.snapshotRate;
/** Piso do buffer. Rede boa NEVER paga mais atraso do que isto. */
const BUFFER_FLOOR_MS = INVARIANT_BUDGETS['BUD-interp'].value;
/** Teto. É decisão de JOGO — atraso maior é o adversário mais no passado. */
const BUFFER_CEILING_MS = INVARIANT_BUDGETS['BUD-interp-max'].value;
/** Sobre quanto tempo o atraso dos snapshots é observado para dimensionar o buffer. */
const JITTER_WINDOW_MS = INVARIANT_BUDGETS['BUD-interp-window'].value;

/**
 * Quantos snapshots ficam guardados.
 *
 * Derivado dos orçamentos, NEVER escolhido a dedo (invariante 5): o anel precisa
 * cobrir o MAIOR buffer que o cliente pode pedir — `BUD-interp-max`, e NEVER o piso —
 * mais o par que emoldura o instante desenhado. Dimensionar pelo piso deixaria o
 * instante desenhado cair fora da janela justamente quando o buffer cresce, que é
 * quando a rede já está ruim.
 */
const CAPACITY = Math.ceil(BUFFER_CEILING_MS / SNAPSHOT_INTERVAL_MS) + 2;

/**
 * O quanto o relógio de render pode ficar ATRÁS do alvo antes de saltar.
 *
 * Existe por causa da aba em segundo plano: o navegador para de entregar frame, o
 * relógio local NEVER anda, e os snapshots continuam chegando. Quando a aba volta, o
 * atraso acumulado é de segundos — recuperar andando seria câmera lenta até alcançar.
 * Um intervalo de snapshot de folga absorve jitter de rede sem saltar à toa.
 */
const MAX_LAG_MS = SNAPSHOT_INTERVAL_MS;

/**
 * O maior delta que ainda é um FRAME.
 *
 * Acima disto não houve frame lento: houve buraco — aba em segundo plano, tela
 * bloqueada, ou o próprio carregamento da página, onde compilar shader come centenas
 * de milissegundos antes do primeiro desenho. Passar o buraco inteiro para o relógio
 * o joga além do dado e SECA o buffer, gastando um solavanco no primeiro segundo de
 * toda partida — MEDIDO no browser, com `solavancos` marcando 1 antes de o jogador
 * encostar no controle.
 *
 * Um intervalo de `BUD-snapshot` é generoso de propósito: aparelho de entrada tem
 * frame lento de VERDADE, e frame lento NEVER pode ser confundido com buraco — o
 * jogo dele ficaria em câmera lenta.
 */
const MAX_FRAME_MS = SNAPSHOT_INTERVAL_MS;

/**
 * O atraso além do qual a CHEGADA do snapshot salta o relógio, sem esperar frame.
 *
 * Existe por um defeito MEDIDO em 5 celulares reais
 * (`docs/testes/2026-08-15-o-ima-trava-e-solta-sozinho-e-trava-ate-.md`): toda a
 * recuperação morava em `advance()`, que só é chamado pelo LAÇO DE FRAME — e o laço
 * de frame é exatamente o que para. Com o `rAF` parado, o WebSocket continua
 * entregando snapshot, `newestMs` cresce, `renderMs` congela, e NEVER existe
 * ninguém acordado para perceber: o conserto estava no lugar onde o defeito NEVER
 * podia se auto-observar. MEDIDO: buffer de 47 000 ms com `snaps: 1` em 90 s, e a
 * `rate` pregada no teto de `BUD-interp-rate` — a +5%, essa dívida levaria ~15 min.
 *
 * O valor sai dos orçamentos (invariante 5): `BUD-interp-max` é o atraso máximo que
 * o JOGO aceita, e os dois intervalos de folga são o dente-de-serra natural — o
 * buffer sobe um intervalo a cada snapshot que chega antes do próximo frame, e
 * saltar nesse repique daria solavanco a 15 Hz em rede perfeita.
 */
const RECOVERY_LAG_MS = BUFFER_CEILING_MS + 2 * SNAPSHOT_INTERVAL_MS;

/**
 * O quanto o tempo dos outros pode ser acelerado ou freado para manter o atraso
 * em `BUD-interp`. Vem do orçamento, NEVER escolhido a dedo (invariante 5).
 */
const MAX_RATE_DEVIATION = INVARIANT_BUDGETS['BUD-interp-rate'].value / 100;

/**
 * Em quanto tempo o relógio quita o erro que tem, quando o desvio NEVER está no teto.
 *
 * Um `BUD-interp` inteiro de propósito: corrigir mais rápido que isso torraria a
 * margem de desvio por causa de jitter comum, e o desvio existe para deriva de
 * RITMO — que é lenta e permanente — e NEVER para solavanco de um snapshot.
 */
const CORRECTION_WINDOW_MS = BUFFER_FLOOR_MS;

/** Só o que o render precisa. Estrutural: encaixa em `PlayerView` sem `render/` ver `net/`. */
export interface InterpolatedPlayer {
  id: number;
  x: number;
  z: number;
  hp: number;
  /**
   * Para onde o jogador aponta o campo. OPCIONAL no tipo e SEMPRE escrito por
   * `sample`: o fio carrega mira desde o `PROTOCOL_VERSION` 3, e quem lê isto —
   * `PlayerView` — usa a ausência para dizer "NEVER gire este avatar". Manter o campo
   * opcional é o que permite ao render distinguir sem mira de mira zerada, e (0, 0) é
   * a direção que faria todo mundo olhar para o norte a partida inteira.
   */
  facingX?: number;
  facingZ?: number;
}

/**
 * Um corpo no instante desenhado.
 *
 * `kind` sai daqui como CÓDIGO, do jeito que veio no fio. Traduzir é trabalho de quem
 * desenha, e é lá que mora a regra de pular corpo de tipo desconhecido — um relógio
 * que decide o que existe na tela seria um relógio com opinião sobre conteúdo.
 */
export interface InterpolatedBody {
  id: number;
  kind: number;
  x: number;
  z: number;
  heldBy: number;
  claims: number;
}

/** Um snapshot guardado, já no relógio da autoridade. */
interface Frame {
  timeMs: number;
  count: number;
  players: InterpolatedPlayer[];
  bodyCount: number;
  bodies: InterpolatedBody[];
}

function emptyBody(): InterpolatedBody {
  return { id: 0, kind: 0, x: 0, z: 0, heldBy: 0, claims: 0 };
}

function emptyFrame(): Frame {
  const players: InterpolatedPlayer[] = [];
  for (let i = 0; i < balance.room.maxPlayers; i++) {
    players.push({ id: 0, x: 0, z: 0, hp: 0, facingX: 0, facingZ: -1 });
  }
  // Corpo nasce vazio e cresce com a sala: ver o cabeçalho. `maxPlayers` é contrato de
  // assento; quantidade de corpo é conteúdo, e conteúdo NEVER tem número aqui.
  return { timeMs: 0, count: 0, players, bodyCount: 0, bodies: [] };
}

function findIn(frame: Frame, id: number): InterpolatedPlayer | undefined {
  for (let i = 0; i < frame.count; i++) {
    const player = frame.players[i]!;
    if (player.id === id) return player;
  }
  return undefined;
}

/**
 * A mira do instante desenhado, entre a de dois quadros.
 *
 * Interpola as componentes e NORMALIZA — o `nlerp`, e NEVER o `slerp`: entre dois
 * snapshots o arco é pequeno, e a diferença de velocidade angular entre os dois é
 * invisível contra o custo de dois `acos` e dois `sin` por jogador por frame.
 *
 * O caso degenerado é a meia-volta EXATA: o sim vira a mira num tick só, então dois
 * quadros podem trazer direções opostas, e no meio do caminho as componentes se
 * cancelam. Vetor de comprimento zero NEVER tem ângulo — girar para um lado ou para o
 * outro seria o cliente inventando por onde o outro jogador virou. Segura a de origem,
 * que é o passado que a autoridade DE FATO teve, e deixa a virada acontecer no quadro
 * seguinte.
 */
function writeFacing(
  slot: InterpolatedPlayer,
  from: InterpolatedPlayer,
  to: InterpolatedPlayer | undefined,
  t: number,
): void {
  const fromX = from.facingX ?? 0;
  const fromZ = from.facingZ ?? 0;
  if (to === undefined || to.facingX === undefined || to.facingZ === undefined) {
    slot.facingX = fromX;
    slot.facingZ = fromZ;
    return;
  }
  const x = fromX + (to.facingX - fromX) * t;
  const z = fromZ + (to.facingZ - fromZ) * t;
  const length = Math.hypot(x, z);
  if (length === 0) {
    slot.facingX = fromX;
    slot.facingZ = fromZ;
    return;
  }
  slot.facingX = x / length;
  slot.facingZ = z / length;
}

function findBodyIn(frame: Frame, id: number): InterpolatedBody | undefined {
  for (let i = 0; i < frame.bodyCount; i++) {
    const body = frame.bodies[i]!;
    if (body.id === id) return body;
  }
  return undefined;
}

/**
 * Uso, por frame do cliente:
 *   1. `record(snapshot)` quando a autoridade fala.
 *   2. `advance(deltaMs)` uma vez por frame, com o delta do laço de render.
 *   3. `sample(view)` e `sampleBodies(bodies)` para escrever o que vai para a tela.
 *
 * O próprio avatar TAMBÉM sai daqui, e é de propósito: com `?predict=0` o dono cai na
 * interpolação, que é o comportamento certo para quem não prevê. Quando a predição
 * está ligada, quem chama sobrescreve o próprio avatar DEPOIS — palpite do presente
 * vale mais que verdade do passado, mas só para quem tem a intenção na mão.
 */
export class Interpolator {
  private readonly frames: Frame[] = [];
  /** Índice do MAIS ANTIGO no anel. */
  private head = 0;
  private size = 0;
  /** Relógio de render, no tempo da autoridade. Monótono: NEVER volta. */
  private renderMs = 0;
  private started = false;
  private stale = 0;
  private starving = false;
  /** Ficou sem passado para desenhar: o buffer precisa ser refeito quando o dado voltar. */
  private reanchor = false;
  private reanchors = 0;
  /** Ritmo do relógio de render. 1 é tempo real; sai disso para quitar deriva. */
  private rate = 1;
  /** Vezes que o relógio ficou atrasado demais e teve que SALTAR para a frente. */
  private snaps = 0;
  /** Relógio local, em tempo de FRAME. Só serve para medir atraso de chegada. */
  private localMs = 0;
  /** Chegada e atraso do último snapshot, para medir o atraso do PRÓXIMO. */
  private lastArrivalMs: number | undefined;
  private lastArrivalTimeMs = 0;
  /**
   * Atrasos observados na janela de `BUD-interp-window`. Guarda quando e quanto:
   * o que dimensiona o buffer é o PIOR atraso ainda dentro da janela.
   */
  private readonly lateness: { atMs: number; ms: number }[] = [];
  /**
   * Atraso que o cliente está pedindo hoje. Anda entre o piso e o teto.
   * Anotado como `number` de propósito: os orçamentos gerados são `as const`, e sem a
   * anotação o TypeScript prende o campo no literal do piso.
   */
  private bufferMsTarget: number = BUFFER_FLOOR_MS;

  constructor() {
    for (let i = 0; i < CAPACITY; i++) this.frames.push(emptyFrame());
  }

  /** Falso até o primeiro snapshot: antes dele NEVER existe passado para desenhar. */
  get ready(): boolean {
    return this.started;
  }

  /** Instante que a tela está mostrando, no relógio da autoridade. */
  get renderTimeMs(): number {
    return this.renderMs;
  }

  /** Snapshots guardados. Bate em `CAPACITY` com a rede saudável. */
  get buffered(): number {
    return this.size;
  }

  /** Snapshots recusados por chegarem fora de ordem. Sintoma de rede, NEVER erro. */
  get staleSnapshots(): number {
    return this.stale;
  }

  /**
   * Quantas vezes o buffer teve que ser refeito depois de secar. Acima de zero
   * significa rede pior do que `BUD-interp` cobre — cada um é um solavanco visto.
   */
  get reanchorCount(): number {
    return this.reanchors;
  }

  /** Vezes que o relógio saltou para a frente por estar atrasado demais. */
  get snapCount(): number {
    return this.snaps;
  }

  /**
   * Solavancos VISTOS, que é o que interessa a quem joga: as duas paredes do relógio
   * dão pulo na tela, e o jogador NEVER sabe qual delas foi. Somar é o honesto —
   * separar vira número de programador em cima do jogo.
   */
  get joltCount(): number {
    return this.reanchors + this.snaps;
  }

  /**
   * O relógio alcançou o snapshot mais novo e parou nele: acabou o passado para
   * desenhar. Congelado é feio, mas é HONESTO — o outro jogador ficou onde a
   * autoridade o deixou, e NEVER onde este cliente imaginou que ele iria.
   */
  get starved(): boolean {
    return this.starving;
  }

  /** Distância entre o instante desenhado e o snapshot mais novo. Vai ao HUD. */
  get bufferMs(): number {
    return this.size === 0 ? 0 : this.newest().timeMs - this.renderMs;
  }

  /**
   * Ritmo atual do relógio de render. 1 é tempo real; fora disso o cliente está
   * quitando deriva entre o tick da sala e o frame do aparelho. Encostado no teto de
   * `BUD-interp-rate` por muito tempo significa deriva maior do que o orçamento cobre.
   */
  get rateNow(): number {
    return this.rate;
  }

  /**
   * Atraso que o cliente está PEDINDO, contra o que ele tem (`bufferMs`). No piso
   * (`BUD-interp`) a rede está boa; encostado no teto (`BUD-interp-max`) o jitter
   * passou do que o jogo aceita pagar, e o que sobra é congelar.
   */
  get bufferTargetMs(): number {
    return this.bufferMsTarget;
  }

  /** Pior atraso de chegada ainda dentro da janela de `BUD-interp-window`. */
  get jitterMs(): number {
    let worst = 0;
    for (const sample of this.lateness) {
      if (sample.ms > worst) worst = sample.ms;
    }
    return worst;
  }

  record(snapshot: AuthoritativeSnapshot): void {
    const timeMs = snapshot.tick * TICK_MS;
    const isNewest = this.size === 0 || timeMs > this.newest().timeMs;
    this.observeArrival(timeMs);

    // Chegou fora de ordem — e fora de ordem NEVER quer dizer inútil. Enquanto ele
    // couber na janela guardada, é dado que o instante desenhado ainda vai atravessar,
    // e jogá-lo fora é abrir um buraco que buffer nenhum preenche. MEDIDO: com jitter
    // de 1,5 intervalo de `BUD-snapshot`, descartar rendia 32 snapshots perdidos e a
    // tela congelando; guardando no LUGAR dele, zero.
    //
    // Só é seguro porque todo snapshot é COMPLETO hoje (`fullSnapshotsOnly` no
    // servidor). No dia em que existir snapshot delta, o atrasado passa a depender do
    // anterior e esta porta tem que fechar de novo.
    if (!isNewest) {
      const oldest = this.frameAt(0);
      // Velho demais: o instante desenhado já passou dele, e ele só tomaria o lugar
      // de um quadro que ainda serve.
      if (timeMs <= oldest.timeMs || this.hasFrameAt(timeMs)) {
        this.stale += 1;
        return;
      }
      this.insertOutOfOrder(snapshot, timeMs);
      return;
    }

    this.writeInto(this.claimFrame(), snapshot, timeMs);

    if (!this.started) {
      this.started = true;
      // Nasce um buffer atrás: o primeiro quadro já sai no atraso de regime, em vez
      // de sair adiantado e ser puxado para trás — que seria o único rebobinar visível.
      this.renderMs = timeMs - this.bufferMsTarget;
      return;
    }

    if (this.reanchor) {
      // O relógio tinha alcançado o dado e parado nele: buffer ZERO. Sem refazer o
      // atraso aqui, ele ficaria em zero para sempre — a interpolação viraria "desenha
      // o snapshot que chegou", de volta aos pulos de `BUD-snapshot` que esta fatia
      // existe para tirar, e sem nada na tela dizendo que degradou.
      //
      // Refazer o buffer é rebobinar, e rebobinar é a ÚNICA coisa que este relógio
      // não faz em regime. Paga-se uma vez, no instante em que a rede já tinha
      // congelado a tela de qualquer jeito. Suavizar isto (dilatar o tempo em vez de
      // saltar) é fatia própria, irmã da correção não suavizada da predição.
      this.reanchor = false;
      this.starving = false;
      this.reanchors += 1;
      this.renderMs = timeMs - this.bufferMsTarget;
      return;
    }

    // O relógio ficou para trás DEMAIS, e quem percebe é a chegada do snapshot.
    //
    // Este é o irmão do salto de `advance()`, e ele existe porque aquele NEVER pode
    // ser alcançado quando o `rAF` para: o WebSocket é o ÚNICO relógio que continua
    // vivo com a aba em segundo plano, a tela apagando ou o `rAF` engasgado. Sem
    // isto, o atraso cresce 1:1 com o relógio de parede e NEVER volta sozinho —
    // MEDIDO em aparelho real, 98 s de buffer sem uma única recuperação.
    //
    // ALWAYS para a FRENTE, e ALWAYS pelo alvo vigente: recuar seria rebobinar, que
    // é a única coisa que este relógio NEVER faz em regime.
    if (timeMs - this.renderMs > RECOVERY_LAG_MS) {
      this.renderMs = timeMs - this.bufferMsTarget;
      this.starving = false;
      // MESMO contador do salto de `advance()`: para quem joga é o mesmo solavanco,
      // e separar viraria número de programador em cima do jogo.
      this.snaps += 1;
    }
  }

  /**
   * Anda o relógio de render com o tempo local e o mantém dentro da janela guardada.
   *
   * O relógio é LOCAL de propósito: entre dois snapshots é o único tempo que existe,
   * e é ele que faz o outro jogador andar em todo frame em vez de pular a 15 Hz.
   */
  advance(deltaMs: number): void {
    if (!this.started) return;
    const newestMs = this.newest().timeMs;
    // Relógio local em tempo de FRAME: o buraco entra capado aqui também, senão a
    // medida de atraso o leria como rede ruim.
    this.localMs += deltaMs > MAX_FRAME_MS ? MAX_FRAME_MS : deltaMs;

    if (deltaMs > MAX_FRAME_MS) {
      // Buraco, NEVER frame. O relógio LOCAL perdeu tempo que a sala não perdeu:
      // somar o buraco inteiro passaria do dado e secaria o buffer, gastando um
      // solavanco que a REDE nunca causou. Reancora para a frente e NEVER conta —
      // `reanchorCount` é medida de rede, e sujá-lo com aba trocada faria dele um
      // número que ninguém mais olha.
      //
      // ALWAYS para a frente: com o buffer no piso da oscilação, o alvo pode estar
      // ATRÁS de onde o relógio já está, e ir até ele seria rebobinar à toa.
      const target = newestMs - this.bufferMsTarget;
      if (target > this.renderMs) this.renderMs = target;
    } else {
      // Correção de RITMO, e NEVER de posição.
      //
      // O tick da sala e o frame do aparelho NEVER andam na mesma taxa: sobra sempre
      // uma fração de porcento, e ela não se cansa. Sem corrigir, o atraso caminha
      // sozinho até bater numa das duas paredes — secar em cima, saltar embaixo — e
      // as duas paredes são um pulo que se VÊ. MEDIDO em rede local perfeita: o
      // buffer varria de 6 a 159 ms em 43 s e secava uma vez.
      //
      // A saída é o relógio andar um tico mais rápido quando está atrasado e um tico
      // mais devagar quando está adiantado, sempre dentro de `BUD-interp-rate`. O
      // preço é a velocidade dos outros mudar nessa fração, que ninguém enxerga; o
      // que se compra é a parede nunca chegar.
      //
      // A identidade NEVER é afetada: o ritmo decide QUAL instante desenhar, e nunca
      // o que existe naquele instante. Quem responde isso continua sendo o par de
      // snapshots que emoldura — por isso o gate de identidade segue verde.
      const error = newestMs - this.bufferMsTarget - this.renderMs;
      const wanted = error / CORRECTION_WINDOW_MS;
      const deviation =
        wanted > MAX_RATE_DEVIATION
          ? MAX_RATE_DEVIATION
          : wanted < -MAX_RATE_DEVIATION
            ? -MAX_RATE_DEVIATION
            : wanted;
      this.rate = 1 + deviation;
      this.renderMs += deltaMs * this.rate;
    }
    if (this.renderMs >= newestMs) {
      // Acabou o passado: congela. NEVER extrapola — inventar para onde o outro
      // jogador ia é exatamente o que produz o avatar que anda e volta.
      //
      // Prender o relógio no dado NEVER é o que salva a volta: quem faz isso é o
      // `reanchor` do `record`, e está MEDIDO (mutante 3). O que este pino impede é o
      // relógio MENTIR enquanto está seco — solto, ele passa do dado e `bufferMs`
      // fica negativo, dizendo mostrar um instante que a sala ainda não viveu.
      this.renderMs = newestMs;
      this.starving = true;
      this.reanchor = true;
      return;
    }
    this.starving = false;
    // Atrás demais: salta para o atraso de regime. Recuperar andando seria câmera
    // lenta, e o jogador leria como travamento.
    if (this.renderMs < newestMs - this.bufferMsTarget - MAX_LAG_MS) {
      this.renderMs = newestMs - this.bufferMsTarget;
      this.snaps += 1;
    }
  }

  /**
   * Escreve em `out` o mundo como a autoridade o teve em `renderTimeMs`.
   *
   * A membresia sai do quadro AO OU ANTES do instante desenhado, e NEVER do mais
   * novo: quem entrou aparece quando o relógio chega na entrada dele, e quem saiu
   * fica na tela até o relógio passar da saída. É a mesma identidade valendo para
   * "quem está na sala", e não só para "onde cada um está".
   *
   * A MIRA anda junto, e ela é o único campo que NEVER é uma reta: direção se
   * interpola pelo arco, e NEVER pelo segmento. Interpolar as duas componentes e parar
   * aí encolheria o vetor no meio do caminho — o render tira o ângulo com `atan2`, que
   * NEVER se importa com comprimento, mas um vetor de mira que encolhe é uma bomba
   * armada para o primeiro que multiplicar por ele. Normalizar é o conserto barato.
   */
  sample(out: InterpolatedPlayer[]): void {
    if (this.size === 0) {
      out.length = 0;
      return;
    }

    const { older, newer } = this.bracket();
    const span = newer.timeMs - older.timeMs;
    const t = span > 0 ? (this.renderMs - older.timeMs) / span : 0;

    out.length = older.count;
    for (let i = 0; i < older.count; i++) {
      const from = older.players[i]!;
      const slot = out[i] ?? { id: 0, x: 0, z: 0, hp: 0 };
      const to = findIn(newer, from.id);
      slot.id = from.id;
      // Sem destino, o jogador saiu entre os dois quadros: segura onde ele estava.
      // Continuar a reta seria empurrar um ausente para um lugar onde ele NEVER esteve.
      slot.x = to === undefined ? from.x : from.x + (to.x - from.x) * t;
      slot.z = to === undefined ? from.z : from.z + (to.z - from.z) * t;
      // `hp` NEVER interpola: meia morte não existe. Vale o do instante desenhado.
      slot.hp = from.hp;
      writeFacing(slot, from, to, t);
      out[i] = slot;
    }
  }

  /**
   * Escreve em `out` os CORPOS como a autoridade os teve em `renderTimeMs`.
   *
   * Mesma identidade, mesma membresia e mesma reta do `sample`. A diferença é o que
   * NEVER interpola, e aqui é mais do que o `hp`: `kind`, `heldBy` e `claims` são
   * estado discreto, e a média de dois deles NEVER é um estado que a sala viveu —
   * meio capturado não existe, e `claims` 1,5 é um número que nenhuma decisão sabe
   * ler. Todos valem o do instante desenhado.
   *
   * O corpo previsto NEVER é excluído daqui: ele sai interpolado como qualquer outro,
   * e quem o sobrescreve é o palpite, DEPOIS. É a mesma ordem do próprio avatar, e ela
   * existe para que desligar a predição derrube o cliente na verdade do passado em vez
   * de num buraco.
   */
  sampleBodies(out: InterpolatedBody[]): void {
    if (this.size === 0) {
      out.length = 0;
      return;
    }

    const { older, newer } = this.bracket();
    const span = newer.timeMs - older.timeMs;
    const t = span > 0 ? (this.renderMs - older.timeMs) / span : 0;

    out.length = older.bodyCount;
    for (let i = 0; i < older.bodyCount; i++) {
      const from = older.bodies[i]!;
      const slot = out[i] ?? emptyBody();
      const to = findBodyIn(newer, from.id);
      slot.id = from.id;
      // Sem destino, o corpo saiu do mundo entre os dois quadros: segura onde estava.
      slot.x = to === undefined ? from.x : from.x + (to.x - from.x) * t;
      slot.z = to === undefined ? from.z : from.z + (to.z - from.z) * t;
      slot.kind = from.kind;
      slot.heldBy = from.heldBy;
      slot.claims = from.claims;
      out[i] = slot;
    }
  }

  /**
   * Mede o quanto este snapshot se atrasou, e redimensiona o buffer.
   *
   * O atraso é medido como DIFERENÇA DE DIFERENÇAS: quanto tempo local passou desde o
   * snapshot anterior, menos quanto tempo de SALA separa os dois. Em rede perfeita dá
   * zero, qualquer que seja a latência — o que interessa aqui NEVER é a latência (essa
   * a predição cobre), é a VARIAÇÃO dela, que é o que faz o dado chegar depois de ser
   * preciso. Medir assim também imuniza contra deriva lenta de relógio, porque a
   * deriva entra igual nos dois lados da subtração.
   *
   * Aba trocada NEVER inflaciona o buffer, e NEVER precisou de guarda própria para
   * isso: `localMs` anda capado em `MAX_FRAME_MS`, então um buraco faz o cliente
   * parecer ADIANTADO — atraso negativo, que esta conta descarta. MEDIDO: uma guarda
   * de buraco aqui era mutante que SOBREVIVIA a tudo, porque nunca chegava a decidir
   * nada. Ver `DIS-25`.
   */
  private observeArrival(timeMs: number): void {
    const previousArrival = this.lastArrivalMs;
    const previousTimeMs = this.lastArrivalTimeMs;
    this.lastArrivalMs = this.localMs;
    this.lastArrivalTimeMs = timeMs;
    if (previousArrival === undefined) return;

    const localGap = this.localMs - previousArrival;
    const roomGap = timeMs - previousTimeMs;
    const late = localGap - roomGap;
    // Adiantado NEVER pesa: quem estoura o buffer é o que chega TARDE.
    if (late > 0) this.lateness.push({ atMs: this.localMs, ms: late });
    this.resizeBuffer();
  }

  /**
   * O buffer é o pior atraso ainda dentro da janela, mais um intervalo de snapshot
   * para a cadência normal — preso entre `BUD-interp` e `BUD-interp-max`.
   *
   * Cresce na hora e encolhe quando a janela esquece o atraso: subir tarde é
   * congelamento, e descer cedo é congelar de novo. Assimetria de propósito.
   */
  private resizeBuffer(): void {
    const cutoff = this.localMs - JITTER_WINDOW_MS;
    while (this.lateness.length > 0 && this.lateness[0]!.atMs < cutoff) this.lateness.shift();

    let worst = 0;
    for (const sample of this.lateness) {
      if (sample.ms > worst) worst = sample.ms;
    }

    const wanted = worst + SNAPSHOT_INTERVAL_MS;
    this.bufferMsTarget =
      wanted < BUFFER_FLOOR_MS
        ? BUFFER_FLOOR_MS
        : wanted > BUFFER_CEILING_MS
          ? BUFFER_CEILING_MS
          : wanted;
  }

  private writeInto(frame: Frame, snapshot: AuthoritativeSnapshot, timeMs: number): void {
    frame.timeMs = timeMs;
    frame.count = snapshot.players.length;
    while (frame.players.length < frame.count) {
      frame.players.push({ id: 0, x: 0, z: 0, hp: 0, facingX: 0, facingZ: -1 });
    }
    for (let i = 0; i < frame.count; i++) {
      const incoming = snapshot.players[i]!;
      const slot = frame.players[i]!;
      slot.id = incoming.id;
      slot.x = incoming.x;
      slot.z = incoming.z;
      slot.hp = incoming.hp;
      slot.facingX = incoming.facingX;
      slot.facingZ = incoming.facingZ;
    }

    frame.bodyCount = snapshot.bodies.length;
    while (frame.bodies.length < frame.bodyCount) frame.bodies.push(emptyBody());
    for (let i = 0; i < frame.bodyCount; i++) {
      const incoming = snapshot.bodies[i]!;
      const slot = frame.bodies[i]!;
      slot.id = incoming.id;
      slot.kind = incoming.kind;
      slot.x = incoming.x;
      slot.z = incoming.z;
      slot.heldBy = incoming.heldBy;
      slot.claims = incoming.claims;
    }
  }

  /** Já existe quadro nesse instante? Snapshot repetido NEVER acrescenta nada. */
  private hasFrameAt(timeMs: number): boolean {
    for (let i = 0; i < this.size; i++) {
      if (this.frameAt(i).timeMs === timeMs) return true;
    }
    return false;
  }

  /**
   * Guarda um snapshot atrasado NO LUGAR dele.
   *
   * Escreve na ponta e desce trocando de posição até o anel voltar a ficar ordenado
   * por tempo — que é a única coisa que `bracket` exige. Bolha em array de
   * `CAPACITY` elementos NEVER é custo: são no máximo três trocas de referência, e
   * isto acontece só quando a rede reordena.
   */
  private insertOutOfOrder(snapshot: AuthoritativeSnapshot, timeMs: number): void {
    this.writeInto(this.claimFrame(), snapshot, timeMs);
    for (let i = this.size - 1; i > 0; i--) {
      const here = (this.head + i) % CAPACITY;
      const before = (this.head + i - 1) % CAPACITY;
      if (this.frames[before]!.timeMs <= this.frames[here]!.timeMs) break;
      const swap = this.frames[before]!;
      this.frames[before] = this.frames[here]!;
      this.frames[here] = swap;
    }
  }

  private newest(): Frame {
    return this.frames[(this.head + this.size - 1) % CAPACITY]!;
  }

  private frameAt(offset: number): Frame {
    return this.frames[(this.head + offset) % CAPACITY]!;
  }

  /** O quadro que vai receber o snapshot novo. Cheio, o mais antigo é reciclado. */
  private claimFrame(): Frame {
    if (this.size < CAPACITY) {
      this.size += 1;
      return this.newest();
    }
    const recycled = this.frameAt(0);
    this.head = (this.head + 1) % CAPACITY;
    return recycled;
  }

  /**
   * O par que emoldura o instante desenhado. Fora da janela, os dois são a ponta —
   * `t` vira zero e o resultado é o quadro da ponta, parado e verdadeiro.
   */
  private bracket(): { older: Frame; newer: Frame } {
    const oldest = this.frameAt(0);
    if (this.renderMs <= oldest.timeMs) return { older: oldest, newer: oldest };
    const newest = this.newest();
    if (this.renderMs >= newest.timeMs) return { older: newest, newer: newest };
    for (let i = 0; i < this.size - 1; i++) {
      const older = this.frameAt(i);
      const newer = this.frameAt(i + 1);
      if (this.renderMs >= older.timeMs && this.renderMs <= newer.timeMs) return { older, newer };
    }
    return { older: newest, newer: newest };
  }
}
